
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackApplyClick } from '../lib/analytics';
import { supabase } from '../lib/supabaseClient';

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const [applicationsOpen, setApplicationsOpen] = useState<boolean | null>(null);

  useEffect(() => {
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'applications_open')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value !== undefined) setApplicationsOpen(data.value === 'true');
      });
  }, []);

  const handleApplyClick = () => {
    trackApplyClick();
    navigate('/apply');
  };

  return (
    <section className="relative overflow-hidden pt-16 pb-24 lg:pt-32 lg:pb-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Content */}
          <div className="flex-1 text-center lg:text-left space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-semibold">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
              </span>
              Creators earn monthly base + bonuses
            </div>

            <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight text-gray-900 leading-[1.1]">
              Get paid to <span className="gradient-text">create</span> wellness content
            </h1>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0">
              We're hiring UGC creators to deliver short-form wellness and personal growth videos for our official Queenli accounts. No posting to your own profile required.
            </p>

            {applicationsOpen && (
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button
                  onClick={handleApplyClick}
                  className="w-full sm:w-auto px-8 py-4 bg-[#341368] text-white rounded-full text-lg font-bold hover:bg-purple-800 transition-all hover:shadow-2xl hover:shadow-purple-500/20 active:scale-95 text-center"
                >
                  Apply to Be a Creator
                </button>
              </div>
            )}

            <div className="flex items-center justify-center lg:justify-start gap-4 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <img
                    key={i}
                    src={`https://images.unsplash.com/photo-${i === 1 ? '1494790108377-be9c29b29330' : i === 2 ? '1438761681033-6461ffad8d80' : i === 3 ? '1534528741775-53994a69daeb' : '1544005313-94ddf0286df2'}?auto=format&fit=crop&q=80&w=100&h=100`}
                    className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    alt="Creator"
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Join <span className="font-bold">wellness creators</span> delivering content weekly
              </p>
            </div>
          </div>

          {/* Visuals */}
          <div className="flex-1 relative">
            <div className="relative z-10 w-full max-w-[340px] mx-auto group">
              {/* Phone Frame */}
              <div className="relative rounded-[3rem] border-8 border-gray-900 overflow-hidden shadow-2xl aspect-[9/16] bg-gray-200">
                <img
                  src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800"
                  alt="Wellness Creator Filming"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Simulated Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6 text-white space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center font-bold text-[10px] border border-white/30">Q</div>
                    <div className="space-y-1">
                      <div className="h-2 w-20 bg-white rounded" />
                      <div className="h-2 w-12 bg-white/20 rounded" />
                    </div>
                  </div>
                  <div className="h-3 w-full bg-white/10 rounded" />
                  <div className="h-3 w-2/3 bg-white/10 rounded" />
                </div>
              </div>

              {/* Floating Reactions */}
              <div className="absolute -right-8 top-20 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-3 animate-bounce shadow-purple-500/10 border border-gray-50">
                <div className="text-pink-500 text-xl">&#10024;</div>
                <div>
                  <div className="text-xs font-bold text-gray-900">4.2k Likes</div>
                  <div className="text-[10px] text-gray-500">Official Channel Reach</div>
                </div>
              </div>

              <div className="absolute -left-12 bottom-32 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-3 animate-pulse delay-700 shadow-purple-500/10 border border-gray-50">
                <div className="text-purple-500 text-xl">&#128156;</div>
                <div>
                  <div className="text-xs font-bold text-gray-900">124 Comments</div>
                  <div className="text-[10px] text-gray-500">"I needed this today"</div>
                </div>
              </div>
            </div>

            {/* Decorative Blobs */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-purple-400/20 blur-[100px] rounded-full -z-10" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-400/20 blur-[80px] rounded-full -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
